import type { ComponentType } from "react";
import { Rocket, Bot, ShieldCheck, Sparkles } from "lucide-react";

export type PromotionAudience = "global" | "seller" | "buyer" | "business";
export type PromotionCategory = "subscriptions" | "boosts" | "ai" | "protection";

export type Promotion = {
  id: string;
  title: string;
  description: string;
  audience: PromotionAudience[];
  category: PromotionCategory;
  ctaLabel: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
};

const PROMOTION_DEFINITIONS: Promotion[] = [
  {
    id: "upgrade-pro-seller",
    title: "Upgrade to Pro Seller",
    description: "Unlock auto-boosts, performance analytics, and monthly Salesman AI credits inside your dashboard.",
    audience: ["seller", "business"],
    category: "subscriptions",
    ctaLabel: "See plans",
    href: "/pricing",
    icon: Rocket,
  },
  {
    id: "salesman-ai",
    title: "Deploy Salesman AI",
    description: "Let our AI concierge match your listing with high-intent buyers from saved wants and alerts.",
    audience: ["seller"],
    category: "ai",
    ctaLabel: "Activate agent",
    href: "/dashboard?salesman=promo",
    icon: Bot,
  },
  {
    id: "verified-trader",
    title: "Earn the Verified Trader badge",
    description: "Get early listing access, ad-free browsing, and trust-first placement across the marketplace.",
    audience: ["global", "buyer", "seller"],
    category: "subscriptions",
    ctaLabel: "Join verified tier",
    href: "/pricing#verified",
    icon: ShieldCheck,
  },
  {
    id: "listo-protected",
    title: "Offer Listo Protected escrow",
    description: "Build instant credibility with secure payments held in escrow and dedicated dispute support.",
    audience: ["seller", "buyer", "business"],
    category: "protection",
    ctaLabel: "Learn about escrow",
    href: "/help#escrow",
    icon: Sparkles,
  },
];

export type PromotionQuery = {
  audience?: PromotionAudience | PromotionAudience[];
  categories?: PromotionCategory[];
  excludeIds?: string[];
  limit?: number;
};

const normalizeAudience = (audience?: PromotionAudience | PromotionAudience[]) => {
  if (!audience) return undefined;
  return Array.isArray(audience) ? audience : [audience];
};

export function getPromotions(query: PromotionQuery = {}): Promotion[] {
  const { audience, categories, excludeIds, limit } = query;
  const audienceFilter = normalizeAudience(audience);

  let candidates = PROMOTION_DEFINITIONS;

  if (audienceFilter && audienceFilter.length > 0) {
    candidates = candidates.filter((promotion) =>
      promotion.audience.some((segment) => audienceFilter.includes(segment))
    );
  }

  if (categories && categories.length > 0) {
    candidates = candidates.filter((promotion) => categories.includes(promotion.category));
  }

  if (excludeIds && excludeIds.length > 0) {
    candidates = candidates.filter((promotion) => !excludeIds.includes(promotion.id));
  }

  if (limit && limit > 0) {
    candidates = candidates.slice(0, limit);
  }

  return candidates;
}
